import React from "react";
import FormMessage from "lib/components/FormMessage";

import st from "lib/utils/index";
import * as FormControl from "lib/components/FormControl";
import { ProductOrderFieldDecorator, ProductOrderFieldListOptionDecorator } from "lib/decorators/index";

export default class ProductPricedOrderFieldFormSection extends React.PureComponent {
  handlePropChange(prop, value) {
    const { product_order_field, onChange, externalSetState, updateProduct, product } = this.props;
    let productSettings = {
      ...product_order_field,
      [prop]: value,
    };
    // db can't save option with another type and same id
    if (prop == "type" && product_order_field.id) {
      delete productSettings.id;
      delete productSettings.uuid;
      productSettings = ProductOrderFieldDecorator.build({
        ...productSettings,
        list_options: [
          ProductOrderFieldListOptionDecorator.build({
            price: {
              value: 0,
              currency: product.price_currency,
            },
            price_before_discount: {
              value: 0,
              currency: product.price_currency,
            },
            position: productSettings.list_options
              ? Math.max(0, ...productSettings.list_options.map((lo) => lo.position)) + 1
              : 0,
          }),
        ],
      });
      externalSetState({
        activeFieldUuid: productSettings.uuid,
      });
      const newProduct = {
        ...product,
        product_order_fields: product.product_order_fields.map((field) =>
          field.uuid == product_order_field.uuid ? productSettings : field
        ),
      };
      updateProduct(newProduct);
    } else {
      const nextProductOrderField = new ProductOrderFieldDecorator(productSettings);
      nextProductOrderField.normalize();
      onChange(nextProductOrderField);
    }
  }

  handleTranslationPropChange(prop, value) {
    const { product_order_field, editedLocale, onChange } = this.props;
    const nextProductOrderField = new ProductOrderFieldDecorator({
      ...product_order_field,
      translations: product_order_field.translations
        .filter((t) => t.locale !== editedLocale)
        .concat([{ ...product_order_field.getTranslation(editedLocale), [prop]: value }]),
    });
    onChange(nextProductOrderField);
  }

  handleListOptionPropChange(listOption, prop, value) {
    const { product_order_field, onChange } = this.props;
    const nextProductOrderField = new ProductOrderFieldDecorator({
      ...product_order_field,
      list_options: product_order_field.list_options.map((option) =>
        option.uuid !== listOption.uuid
          ? option
          : new ProductOrderFieldListOptionDecorator({
              ...option,
              [prop]: value,
            })
      ),
    });
    nextProductOrderField.normalize();
    onChange(nextProductOrderField);
  }

  handleListOptionTranslationPropChange(listOption, prop, value) {
    const { product_order_field, editedLocale, onChange } = this.props;
    const nextProductOrderField = new ProductOrderFieldDecorator({
      ...product_order_field,
      list_options: product_order_field.list_options.map((option) =>
        option.uuid !== listOption.uuid
          ? option
          : new ProductOrderFieldListOptionDecorator({
              ...option,
              translations: option.translations
                .filter((t) => t.locale !== editedLocale)
                .concat([{ ...option.getTranslation(editedLocale), [prop]: value }]),
            })
      ),
    });
    onChange(nextProductOrderField);
  }

  handleListOptionRemove(listOption) {
    const { product_order_field, onChange, onBlur } = this.props;
    const list_options = product_order_field.list_options.filter((option) => option.uuid !== listOption.uuid);
    const nextProductOrderField = new ProductOrderFieldDecorator({
      ...product_order_field,
      list_options,
    });
    nextProductOrderField.normalize();
    onChange(nextProductOrderField);
    onBlur();
  }

  handleListOptionAdd() {
    const { product, product_order_field, onChange } = this.props;
    const list_options = product_order_field.list_options.concat([
      ProductOrderFieldListOptionDecorator.build({
        price: {
          value: 0,
          currency: product.price_currency,
        },
        price_before_discount: {
          value: 0,
          currency: product.price_currency,
        },
        position: Math.max(0, ...product_order_field.list_options.map((lo) => lo.position)) + 1,
      }),
    ]);
    const nextProductOrderField = new ProductOrderFieldDecorator({
      ...product_order_field,
      list_options,
    });
    nextProductOrderField.normalize();
    onChange(nextProductOrderField);
  }

  render() {
    const { readOnlyAccess, product, product_order_field, editedLocale, errors, name, onRemove, onBlur, onClose } =
      this.props;
    const assignationTypeOptions = [
      ["assignation_per_order", I18n.humanAttributeName("product_order_field", "assignation_per_order")],
      ["assignation_per_person", I18n.humanAttributeName("product_order_field", "assignation_per_person")],
    ];
    const assignedVariantsIds =
      product_order_field.product_variants_assignation_type === "product_variants_assignation_all"
        ? null
        : product_order_field.product_variants_ids || [];

    return (
      <div>
        <FormMessage error={errors[name]} />
        <FormControl.InputFormGroup
          {...FormControl.addProps(`${name}.name`, { errors })}
          value={product_order_field.getTranslation(editedLocale).name}
          onBlur={onBlur}
          onChange={this.handleTranslationPropChange.bind(this, "name")}
          label={I18n.humanAttributeName("product_order_field", "name")}
          required
          autoFocus={product_order_field._focus}
          readOnly={readOnlyAccess}
        />

        {(product.product_variants.length > 1 ||
          product_order_field.product_variants_assignation_type === "product_variants_assignation_some") && (
          <FormControl.SelectLimitFormGroup
            value={assignedVariantsIds}
            onBlur={onBlur}
            onChange={(ids) =>
              !!assignedVariantsIds !== !!ids
                ? this.handlePropChange(
                    "product_variants_assignation_type",
                    ids ? "product_variants_assignation_some" : "product_variants_assignation_all"
                  )
                : this.handlePropChange("product_variants_ids", ids)
            }
            label={I18n.t("papp.components.ProductPricedOrderFieldFormSection.for_all_variants")}
            options={product.product_variants.filter((pv) => pv.id).map((pv) => [pv.id, pv.name])}
            readOnly={readOnlyAccess}
          />
        )}

        <FormControl.InputFormGroup
          {...FormControl.addProps(`${name}.customer_hint`, { errors })}
          value={product_order_field.getTranslation(editedLocale).customer_hint}
          onBlur={onBlur}
          onChange={this.handleTranslationPropChange.bind(this, "customer_hint")}
          label={I18n.humanAttributeName("product_order_field", "customer_hint")}
          placeholder={I18n.humanAttributeName("product_order_field", "customer_hint_placeholder")}
          readOnly={readOnlyAccess}
        />

        <div className="field">
          <label htmlFor={`${name}.type`}>
            {I18n.t("papp.components.ProductPricedOrderFieldFormSection.value_type")}
          </label>

          <div className="fields inline u-alignItemsFlexStart">
            <div className="field">
              <FormControl.Select
                error={!!errors[`${name}.type`]}
                value={product_order_field.type}
                onBlur={onBlur}
                onChange={this.handlePropChange.bind(this, "type")}
                options={ProductOrderFieldDecorator.priced_field_types}
                // disabled={!!product_order_field.id}
                readOnly={readOnlyAccess}
              />
            </div>

            {product_order_field.type === "ProductOrderField::Number" && (
              <div className="fields inline widths equal">
                <label htmlFor={`${name}.min_value`}>{I18n.t("base.from")}</label>
                <FormControl.Input
                  id={`${name}.min_value`}
                  error={!!errors[`${name}.min_value`]}
                  value={product_order_field.min_value}
                  onBlur={onBlur}
                  onChange={this.handlePropChange.bind(this, "min_value")}
                  type="number"
                  min="0"
                  className="u-tinyWidth"
                  readOnly={readOnlyAccess}
                />
                <label htmlFor={`${name}.max_value`}>{I18n.t("base.to")}</label>
                <FormControl.Input
                  id={`${name}.max_value`}
                  error={!!errors[`${name}.max_value`]}
                  value={product_order_field.max_value}
                  onBlur={onBlur}
                  onChange={this.handlePropChange.bind(this, "max_value")}
                  type="number"
                  min="0"
                  className="u-tinyWidth"
                  readOnly={readOnlyAccess}
                />
              </div>
            )}

            {product_order_field.type === "ProductOrderField::List" && (
              <div className="fields grouped flexGrow">
                {errors[`${name}.list_options`] && <FormMessage error={errors[`${name}.list_options`]} />}
                {product_order_field.list_options.map((listOption) => (
                  <div className="field inline displayFlex" key={listOption.uuid}>
                    <FormControl.Input
                      error={!!errors[`${name}.list_options[${listOption.uuid}].name`]}
                      className="flexGrow"
                      value={listOption.getTranslation(editedLocale).name}
                      onBlur={onBlur}
                      onChange={this.handleListOptionTranslationPropChange.bind(this, listOption, "name")}
                      placeholder={I18n.humanAttributeName("list_option", "name")}
                      readOnly={readOnlyAccess}
                    />

                    <FormControl.Money
                      error={!!errors[`${name}.list_options[${listOption.uuid}].price`]}
                      value={listOption.price}
                      onBlur={onBlur}
                      onChange={this.handleListOptionPropChange.bind(this, listOption, "price")}
                      placeholder={I18n.humanAttributeName("list_option", "price")}
                      defaultCurrency={product.price_currency}
                      readOnly={readOnlyAccess}
                    />

                    {!readOnlyAccess && (
                      <a
                        onClick={this.handleListOptionRemove.bind(this, listOption)}
                        className="ui button tiny red basic clear icon"
                      >
                        <i className="icon material-icons">close</i>
                      </a>
                    )}
                  </div>
                ))}

                {!readOnlyAccess && (
                  <div className="field">
                    <a onClick={this.handleListOptionAdd.bind(this)} className="ui button small basic blue">
                      {I18n.t("papp.components.ProductPricedOrderFieldFormSection.add_list_option")}
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <FormControl.MultipleRadioFormGroup
          {...FormControl.addProps(`${name}.assignation_type`, { errors })}
          value={product_order_field.assignation_type}
          onBlur={onBlur}
          onChange={this.handlePropChange.bind(this, "assignation_type")}
          label={I18n.t("papp.components.ProductPricedOrderFieldFormSection.ask_customer")}
          options={assignationTypeOptions}
          inlineLabel
          inline
          readOnly={readOnlyAccess}
        />

        <FormControl.CheckboxFormGroup
          {...FormControl.addProps(`${name}.order_value_required`, { errors })}
          value={product_order_field.order_value_required}
          onBlur={onBlur}
          onChange={this.handlePropChange.bind(this, "order_value_required")}
          label={I18n.humanAttributeName("product_order_field", "order_value_required")}
          toggle
          readOnly={readOnlyAccess}
        />

        <div className="fields inline">
          <label htmlFor={`${name}.price`}>{I18n.humanAttributeName("product_order_field", "price")}</label>

          {product_order_field.type !== "ProductOrderField::List" && (
            <FormControl.Money
              error={errors[`${name}.price`]}
              value={product_order_field.price}
              onBlur={onBlur}
              onChange={this.handlePropChange.bind(this, "price")}
              defaultCurrency={product.price_currency}
              className="u-marginRight"
              readOnly={readOnlyAccess}
            />
          )}
          <FormControl.Select
            error={errors[`${name}.price_type`]}
            value={product_order_field.price_type}
            onBlur={onBlur}
            onChange={this.handlePropChange.bind(this, "price_type")}
            required
            options={ProductOrderFieldDecorator.price_type_options}
            className="u-mediumWidth"
            disabled={product_order_field.assignation_type !== "assignation_per_order"}
            readOnly={readOnlyAccess}
          />
        </div>

        <FormControl.CheckboxFormGroup
          {...FormControl.addProps(`${name}.price_included_in_order_base_amount`, { errors })}
          value={product_order_field.price_included_in_order_base_amount}
          onBlur={onBlur}
          onChange={this.handlePropChange.bind(this, "price_included_in_order_base_amount")}
          label={I18n.humanAttributeName("product_order_field", "price_included_in_order_base_amount")}
          toggle
          disabled={product_order_field.price && product_order_field.price.currency !== product.price_currency}
          help={
            product_order_field.price && product_order_field.price.currency !== product.price_currency
              ? I18n.t("papp.components.ProductPricedOrderFieldFormSection.different_currency_base_amount_note")
              : undefined
          }
          readOnly={readOnlyAccess}
        />

        <FormControl.CheckboxFormGroup
          {...FormControl.addProps(`${name}.show_on_manifest`, { errors })}
          value={product_order_field.show_on_manifest}
          onBlur={onBlur}
          onChange={this.handlePropChange.bind(this, "show_on_manifest")}
          label={I18n.t("papp.components.ProductPricedOrderFieldFormSection.show_on_manifest")}
          toggle
          readOnly={readOnlyAccess}
        />

        {!readOnlyAccess && (
          <a onClick={onRemove} className="ui button small red basic" hidden={product_order_field._new}>
            {I18n.t("papp.components.ProductPricedOrderFieldFormSection.remove")}
          </a>
        )}

        {onClose && (
          <a onClick={onClose} className="ui button small basic right floated">
            Zamknij
          </a>
        )}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/lib/components/FormSection/ProductPricedOrderFieldFormSection.jsx